import * as React from "react";
import { Check, ChevronsUpDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Badge } from "@/components/ui/badge";

export type Option = {
  value: string;
  label: string;
};

interface MultiSelectProps {
  options: Option[];
  defaultValue?: Option[];
  onChange: (selectedOptions: Option[]) => void;
  className?: string;
  placeholder?: string;
  disabled?: boolean;
}

const encodeValue = (value: string, label: string) => `${label}|${value}`;
const decodeValue = (value: string) => value.split("|").at(-1) as string;

export function MultiSelect({
  options,
  defaultValue = [],
  onChange,
  className,
  placeholder = "Select options...",
  disabled = false,
}: MultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const [selected, setSelected] = React.useState<Option[]>(defaultValue);

  const handleSelect = (currentValue: string) => {
    if (disabled) return;
    const decodedValue = decodeValue(currentValue);
    const option = options.find((opt) => opt.value === decodedValue);
    if (!option) return;

    const updatedSelected = selected.some((item) => item.value === option.value)
      ? selected.filter((item) => item.value !== option.value)
      : [...selected, option];
    setSelected(updatedSelected);
    onChange(updatedSelected);
  };

  const handleRemove = (option: Option) => {
    if (disabled) return;
    const updatedSelected = selected.filter(
      (item) => item.value !== option.value,
    );
    setSelected(updatedSelected);
    onChange(updatedSelected);
  };

  const handleClear = () => {
    if (disabled) return;
    setSelected([]);
    onChange([]);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          disabled={disabled}
          className={cn(
            "flex h-auto min-h-10 w-full items-start justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
            "hover:bg-background",
            className,
          )}
        >
          <div className="flex max-h-[120px] flex-wrap gap-1 overflow-y-auto">
            {selected.length > 0 ? (
              selected.map((option) => (
                <Badge
                  key={option.value}
                  variant="secondary"
                  className="my-0.5 rounded-sm px-1 font-normal"
                >
                  {option.label}
                  <span
                    className="ml-1 cursor-pointer ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2"
                    role="button"
                    tabIndex={disabled ? -1 : 0}
                    onKeyDown={(e) => {
                      if (!disabled && e.key === "Enter") {
                        handleRemove(option);
                      }
                    }}
                    onMouseDown={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                    }}
                    onClick={() => handleRemove(option)}
                  >
                    <X className="h-3 w-3 text-muted-foreground hover:text-foreground" />
                  </span>
                </Badge>
              ))
            ) : (
              <span className="text-muted-foreground">{placeholder}</span>
            )}
          </div>
          <ChevronsUpDown className="ml-2 mt-2 h-4 w-4 shrink-0 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0">
        <Command>
          <CommandInput
            placeholder="Search options..."
            className="border-none focus:ring-0"
          />
          <CommandList>
            <CommandEmpty>No option found.</CommandEmpty>
            <CommandGroup>
              {options.map((option) => (
                <CommandItem
                  key={option.value}
                  value={encodeValue(option.value, option.label)}
                  onSelect={handleSelect}
                  disabled={disabled}
                  className="text-sm"
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      selected.some((item) => item.value === option.value)
                        ? "opacity-100"
                        : "opacity-0",
                    )}
                  />
                  {option.label}
                </CommandItem>
              ))}
            </CommandGroup>
            {selected.length > 0 && (
              <Button
                variant="ghost"
                onClick={handleClear}
                disabled={disabled}
                className="w-full justify-center text-sm font-normal"
              >
                Clear all
              </Button>
            )}
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
